/**
 * Input Component
 *
 * A comprehensive, accessible form input component for the BiteWise application.
 * Supports various input types, validation states, and follows WCAG 2.1 AA guidelines.
 * Integrates with the existing design system and theme.
 *
 * @module components/ui/Input
 */

import React, { forwardRef, useState } from 'react';
import { cn } from '@/utils/cn';
import { useTheme } from '@/hooks/useTheme';

/**
 * Input component props interface
 */
export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Input variant for different visual styles */
  variant?: 'default' | 'filled' | 'outlined';
  /** Input size */
  size?: 'sm' | 'md' | 'lg';
  /** Validation state */
  state?: 'default' | 'error' | 'warning' | 'success';
  /** Error message to display */
  error?: string;
  /** Warning message to display */
  warning?: string;
  /** Success message to display */
  success?: string;
  /** Helper text to display below input */
  helperText?: string;
  /** Label for the input */
  label?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Icon to display on the left side */
  leftIcon?: React.ReactNode;
  /** Icon to display on the right side */
  rightIcon?: React.ReactNode;
  /** Loading state */
  loading?: boolean;
  /** Full width */
  fullWidth?: boolean;
  /** Custom container class */
  containerClassName?: string;
  /** Custom label class */
  labelClassName?: string;
  /** Custom message class */
  messageClassName?: string;
}

/**
 * Accessible Input component with validation states and comprehensive styling
 *
 * @example
 * ```tsx
 * <Input
 *   label="Email Address"
 *   type="email"
 *   placeholder="Enter your email"
 *   required
 *   error="Please enter a valid email address"
 *   state="error"
 * />
 * ```
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({
    className,
    containerClassName,
    labelClassName,
    messageClassName,
    variant = 'default',
    size = 'md',
    state = 'default',
    error,
    warning,
    success,
    helperText,
    label,
    required = false,
    leftIcon,
    rightIcon,
    loading = false,
    fullWidth = false,
    id,
    'aria-describedby': ariaDescribedBy,
    ...props
  }, ref) => {
    const { isDark } = useTheme();
    const [isFocused, setIsFocused] = useState(false);

    // Generate unique IDs for accessibility
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
    const errorId = `${inputId}-error`;
    const warningId = `${inputId}-warning`;
    const successId = `${inputId}-success`;
    const helperId = `${inputId}-helper`;

    // Determine which message to show (priority: error > warning > success > helper)
    const message = error || warning || success || helperText;
    const messageId = error ? errorId : warning ? warningId : success ? successId : helperId;

    // Build aria-describedby
    const describedBy = [
      ariaDescribedBy,
      message ? messageId : undefined
    ].filter(Boolean).join(' ') || undefined;

    // Base input classes
    const baseInputClasses = 'w-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1';

    // Variant classes
    const variantClasses = {
      default: cn(
        'border',
        isDark ? 'bg-secondary-800' : 'bg-white'
      ),
      filled: cn(
        'border-0',
        isDark ? 'bg-secondary-700' : 'bg-secondary-100'
      ),
      outlined: cn(
        'border-2 bg-transparent',
        isDark ? 'border-secondary-600' : 'border-secondary-300'
      )
    };

    // Size classes
    const sizeClasses = {
      sm: 'text-sm px-3 py-1.5 min-h-[32px] rounded-md',
      md: 'text-base px-4 py-2 min-h-[40px] rounded-lg',
      lg: 'text-lg px-5 py-3 min-h-[48px] rounded-lg'
    };

    // State classes
    const stateClasses = {
      default: cn(
        'focus:ring-primary-500/50',
        isDark ? 'focus:border-primary-400' : 'focus:border-primary-500'
      ),
      error: cn(
        'focus:ring-red-500/50',
        isDark ? 'border-red-400 focus:border-red-400' : 'border-red-500 focus:border-red-500'
      ),
      warning: cn(
        'focus:ring-yellow-500/50',
        isDark ? 'border-yellow-400 focus:border-yellow-400' : 'border-yellow-500 focus:border-yellow-500'
      ),
      success: cn(
        'focus:ring-green-500/50',
        isDark ? 'border-green-400 focus:border-green-400' : 'border-green-500 focus:border-green-500'
      )
    };

    // Text color classes
    const textClasses = cn(
      isDark ? 'placeholder:text-secondary-400' : 'placeholder:text-secondary-200',
      isDark ? 'text-secondary-100' : 'text-secondary-900'
    );

    // Disabled classes
    const disabledClasses = cn(
      'disabled:opacity-50 disabled:cursor-not-allowed',
      isDark ? 'disabled:bg-secondary-800' : 'disabled:bg-secondary-50'
    );

    // Icon classes
    const iconClasses = cn(
      'flex-shrink-0',
      isDark ? 'text-secondary-400' : 'text-secondary-500'
    );

    // Loading spinner
    const LoadingSpinner = () => (
      <svg
        className="animate-spin h-4 w-4 text-secondary-500"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    );

    return (
      <div className={cn('space-y-1', fullWidth && 'w-full', containerClassName)}>
        {/* Label */}
        {label && (
          <label
            htmlFor={inputId}
            className={cn(
              'block text-sm font-medium',
              isDark ? 'text-secondary-300' : 'text-secondary-700',
              required && 'after:content-["*"] after:ml-0.5 after:text-red-500',
              labelClassName
            )}
          >
            {label}
          </label>
        )}

        {/* Input Container */}
        <div className="relative">
          {/* Left Icon */}
          {leftIcon && (
            <div className={cn(
              'absolute left-3 top-1/2 transform -translate-y-1/2',
              iconClasses
            )}>
              {leftIcon}
            </div>
          )}

          {/* Input */}
          <input
            ref={ref}
            id={inputId}
            aria-describedby={describedBy}
            aria-invalid={state === 'error' ? 'true' : 'false'}
            aria-required={required}
            className={cn(
              baseInputClasses,
              variantClasses[variant],
              sizeClasses[size],
              stateClasses[state],
              textClasses,
              disabledClasses,
              leftIcon && 'pl-10',
              (rightIcon || loading) && 'pr-10',
              isFocused && 'ring-2',
              className
            )}
            onFocus={(e) => {
              setIsFocused(true);
              props.onFocus?.(e);
            }}
            onBlur={(e) => {
              setIsFocused(false);
              props.onBlur?.(e);
            }}
            {...props}
          />

          {/* Right Icon or Loading */}
          {(rightIcon || loading) && (
            <div className={cn(
              'absolute right-3 top-1/2 transform -translate-y-1/2',
              iconClasses
            )}>
              {loading ? <LoadingSpinner /> : rightIcon}
            </div>
          )}
        </div>

        {/* Message */}
        {message && (
          <p
            id={messageId}
            className={cn(
              'text-sm',
              error && (isDark ? 'text-red-400' : 'text-red-600'),
              warning && (isDark ? 'text-yellow-400' : 'text-yellow-600'),
              success && (isDark ? 'text-green-400' : 'text-green-600'),
              !error && !warning && !success && (isDark ? 'text-secondary-400' : 'text-secondary-600'),
              messageClassName
            )}
            role={error ? 'alert' : undefined}
            aria-live={error ? 'polite' : undefined}
          >
            {message}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;